import {ReactElement, useEffect, useState} from "react";
import {ThemeProvider} from "styled-components";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import {Input} from "@zendeskgarden/react-forms";
import {Inline} from "@zendeskgarden/react-loaders";
import {ViziblyTheme} from "../../../styles/zendesk-garden/ViziblyZDGTheme";
import {COLORS} from "../../../constants/Colors";
import * as css from "./styles/DeleteLineItemsModal.module.scss";

const DEFAULT_BULK_DELETE_WARNING_THRESHOLD = 5;
const CONFIRMATION_TEXT = "delete";

export interface DeleteLineItemsModalProps {
    visible: boolean;
    itemIds: string[];
    itemLabel: string;
    itemLabelPlural: string;
    submitting?: boolean;
    bulkDeleteWarningThreshold?: number;
    onDelete: () => Promise<boolean>;
    onClose: () => void;
    onDeleted: () => void;
    onDeleteFailed?: () => void;
}

export function DeleteLineItemsModal(props: DeleteLineItemsModalProps): ReactElement {
    const {
        visible,
        itemIds,
        itemLabel,
        itemLabelPlural,
        submitting = false,
        bulkDeleteWarningThreshold = DEFAULT_BULK_DELETE_WARNING_THRESHOLD,
        onDelete,
        onClose,
        onDeleted,
        onDeleteFailed,
    } = props;

    const [confirmationInput, setConfirmationInput] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if(!visible) {
            setConfirmationInput("");
            setIsDeleting(false);
        }
    }, [visible]);

    async function executeDelete() {
        if(itemIds.length === 0 || isDeleting || (itemIds.length > bulkDeleteWarningThreshold && confirmationInput !== CONFIRMATION_TEXT)) {
            return;
        }

        setIsDeleting(true);

        try {
            const success = await onDelete();
            if(success) {
                onDeleted();
            } else {
                setConfirmationInput("");
                setIsDeleting(false);
                onDeleteFailed?.();
            }
        } catch {
            setConfirmationInput("");
            setIsDeleting(false);
            onDeleteFailed?.();
        }
    }

    function close() {
        if(isDeleting || submitting) {
            return;
        }

        setConfirmationInput("");
        onClose();
    }

    if(!visible) {
        return <></>;
    }

    const itemCountLabel = itemIds.length === 1 ? itemLabel : itemLabelPlural;
    const deleteDisabled = isDeleting || submitting || (itemIds.length > bulkDeleteWarningThreshold && confirmationInput !== CONFIRMATION_TEXT);

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Modal onClose={close}>
                <Header isDanger>Delete {itemIds.length} {itemCountLabel}</Header>
                <Body>
                    <div className={css.confirmContent}>
                        <p className={css.confirmLead}>
                            This will permanently delete{" "}
                            <span className={css.confirmCount}>{itemIds.length}</span>{" "}
                            {itemCountLabel.toLowerCase()}.
                        </p>

                        {itemIds.length > bulkDeleteWarningThreshold && (
                            <>
                                <h5 className={css.confirmPrompt}>Enter &quot;{CONFIRMATION_TEXT}&quot; to confirm.</h5>
                                <Input
                                    type="text"
                                    aria-label={`Enter "${CONFIRMATION_TEXT}" to confirm.`}
                                    placeholder={CONFIRMATION_TEXT}
                                    value={confirmationInput}
                                    onChange={(event) => setConfirmationInput(event.target.value)}
                                    disabled={isDeleting || submitting}
                                />
                                <div className={css.warningWrapper}>
                                    <h5 className={css.warningText}>THIS ACTION CANNOT BE UNDONE</h5>
                                </div>
                            </>
                        )}
                    </div>
                </Body>
                <Footer>
                    <FooterItem>
                        <Button onClick={close} isBasic disabled={isDeleting || submitting}>
                            Cancel
                        </Button>
                    </FooterItem>
                    <FooterItem>
                        <Button isDanger onClick={executeDelete} disabled={deleteDisabled}>
                            {isDeleting
                                ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label={`Deleting ${itemLabelPlural.toLowerCase()}`} />
                                : <span>Delete</span>
                            }
                        </Button>
                    </FooterItem>
                </Footer>
                <Close aria-label="Close modal" />
            </Modal>
        </ThemeProvider>
    );
}
